import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import HeroVisualization from "@/components/HeroVisualization";
import AnalystWorkflowDiagram from "@/components/AnalystWorkflowDiagram";
import AbstractVisual from "@/components/AbstractVisual";
import { ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLandingVariant } from "@/hooks/useLandingVariant";
import {
  trackLandingVariantViewed,
  trackLandingCtaClicked,
  trackServiceCardClicked,
  trackPricingCardClicked,
  trackFaqOpened,
} from "@/lib/landingAnalytics";
import { useEffect, useRef } from "react";

export type LandingVariant = "A" | "B" | "C";

interface VariantConfig {
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaHref: string;
  finalCtaText: string;
  finalCtaHref: string;
  heroLayout: "centered" | "left-aligned";
  problemHeadline: string;
  problemBody: string;
  solutionHeadline: string;
  solutionBody: string;
  trustBadge: string;
  serviceStatus: string;
  builtForHeading: string;
  builtForBody: string;
  serviceLevelsIntro: string;
}

const VARIANT_CONFIGS: Record<LandingVariant, VariantConfig> = {
  A: {
    headline: "Application-Level WordPress Security, Managed by Experts.",
    subheadline:
      "Continuous monitoring, vulnerability intelligence, and practical hardening for WordPress environments — with high-risk alerts reviewed by Australian security analysts.",
    ctaText: "See Technical Details",
    ctaHref: "/technical-details",
    finalCtaText: "Request Free Scan",
    finalCtaHref: "/scan",
    heroLayout: "centered",
    problemHeadline: "Standard security plugins aren't enough.",
    problemBody:
      "Automated-only tools and basic hosting security leave gaps that attackers exploit. Without application-level monitoring, vulnerability intelligence, and human validation, WordPress sites remain exposed to known and emerging threats.",
    solutionHeadline: "A Managed Security Layer for WordPress.",
    solutionBody:
      "ShadowScan combines purpose-built technology with human expertise to provide a dedicated security operations team for your WordPress sites.",
    trustBadge: "Proudly Australian Owned & Operated",
    serviceStatus: "Application-Level WordPress Monitoring",
    builtForHeading: "Built for",
    builtForBody: "Agencies, IT teams, managed WordPress environments, and security-conscious businesses.",
    serviceLevelsIntro: "Choose the monitoring and hardening level that fits your WordPress environment.",
  },
  B: {
    headline: "Keep Your WordPress Site Safe and Your Business Running.",
    subheadline:
      "Managed WordPress security that helps reduce downtime, business disruption, and recovery costs — without adding operational complexity to your team.",
    ctaText: "Get Protected",
    ctaHref: "/services",
    finalCtaText: "Get Protected Today",
    finalCtaHref: "/services",
    heroLayout: "centered",
    problemHeadline: "Can your business afford a security incident?",
    problemBody:
      "A compromised WordPress site can lead to downtime, reputational damage, lost enquiries, and expensive recovery work. Most automated tools don't provide the human oversight needed to identify and prioritise real threats.",
    solutionHeadline: "Security That Works While You Focus on Business.",
    solutionBody:
      "ShadowScan provides managed WordPress security that monitors for threats, validates alerts with human analysts, and provides clear guidance so your site stays up and your business keeps running.",
    trustBadge: "Proudly Australian Owned & Operated",
    serviceStatus: "Managed WordPress Security",
    builtForHeading: "Built for",
    builtForBody: "Businesses that rely on WordPress but don't have an internal security team.",
    serviceLevelsIntro: "Choose the level of support that fits your site, team, and budget.",
  },
  C: {
    headline: "A Real Security Team for Your WordPress Site.",
    subheadline:
      "We don't just send automated alerts; we validate, prioritise, and guide the response. Our Australian-based experts provide direct assistance within the scope of your plan.",
    ctaText: "Explore Our Response Service",
    ctaHref: "/services",
    finalCtaText: "Talk to Our Team",
    finalCtaHref: "/contact",
    heroLayout: "centered",
    problemHeadline: "Who responds when your WordPress site triggers an alert at 2 AM?",
    problemBody:
      "Automated tools send alerts, but they don't respond. Without human analysts to validate and act, security notifications become noise that's impossible to trust.",
    solutionHeadline: "A Managed Security Layer for WordPress.",
    solutionBody:
      "ShadowScan combines purpose-built technology with human expertise to provide a dedicated security operations team for your WordPress sites. Real people, real accountability.",
    trustBadge: "Proudly Australian Owned & Operated",
    serviceStatus: "Human-Led WordPress Security Operations",
    builtForHeading: "Built for",
    builtForBody: "Businesses that want real human support when security incidents happen.",
    serviceLevelsIntro: "Choose the level of human oversight and response support your site needs.",
  },
};

const TRACK_CTA_CLICK = "cta_click";

const useCtaTracking = (variant: LandingVariant) => {
  const trackCta = (location: string, text: string, href: string) => {
    const _paq = (window as unknown as Record<string, unknown>)._paq as
      | unknown[][]
      | undefined;
    _paq?.push([
      "trackEvent",
      "Landing Page",
      TRACK_CTA_CLICK,
      `variant=${variant} location=${location} text=${text} href=${href}`,
    ]);

    trackLandingCtaClicked(variant, text, location, href);
  };
  return trackCta;
};

const Index = () => {
  const variant = useLandingVariant();
  const config = VARIANT_CONFIGS[variant];

  const trackCta = useCtaTracking(variant);

  const trackServiceCta = (serviceName: string, text: string, href: string, section: string) => {
    const _paq = (window as any)._paq as unknown[][] | undefined;
    _paq?.push([
      "trackEvent",
      "Landing Page",
      TRACK_CTA_CLICK,
      `variant=${variant} service=${serviceName} text=${text} href=${href} section=${section}`,
    ]);

    trackServiceCardClicked(variant, serviceName, section);
  };

  const trackPricingCardClick = (planName: string) => {
    const _paq = (window as any)._paq as unknown[][] | undefined;
    _paq?.push(["trackEvent", "Landing Page", "pricing_card_click", planName]);

    trackPricingCardClicked(variant, planName, "featured-plans");
  };

  const trackFaqOpen = (question: string) => {
    if (!question) return; // Accordion may pass empty string on collapse
    const _paq = (window as any)._paq as unknown[][] | undefined;
    _paq?.push(["trackEvent", "Landing Page", "faq_open", question]);

    trackFaqOpened(variant, question);
  };

  // Track variant view once on mount (prevent double-fire in StrictMode)
  const hasTrackedVariantRef = useRef(false);
  useEffect(() => {
    if (hasTrackedVariantRef.current) return;
    hasTrackedVariantRef.current = true;
    trackLandingVariantViewed(variant);
  }, [variant]);

  const howItWorks = [
    {
      step: "1",
      title: "Connect & Scan",
      description:
        "We integrate with your site and perform an initial hardening review.",
    },
    {
      step: "2",
      title: "Monitor & Detect",
      description:
        "Our platform provides continuous monitoring, with high-risk alerts reviewed by human analysts.",
    },
    {
      step: "3",
      title: "Respond & Remediate",
      description:
        "We provide clear guidance and expert assistance to fix vulnerabilities.",
    },
  ];

  const faqs = [
    {
      question: "Will ShadowScan slow down my site?",
      answer:
        "No. Our agent is lightweight and designed for minimal performance impact. Monitoring runs asynchronously and does not add visible latency to page loads.",
    },
    {
      question: "Do I still need a WAF?",
      answer:
        "Yes. A Web Application Firewall is a strong first line of defence. ShadowScan operates at the application level, providing deeper protections that complement your edge security.",
    },
    {
      question: "What happens if we find a vulnerability?",
      answer:
        "We validate the finding with our analysts, provide clear guidance on remediation, and assist with the fix if your plan includes incident response hours.",
    },
    {
      question: "Does this work with any WordPress hosting provider?",
      answer:
        "Yes. ShadowScan integrates with self-hosted WordPress, managed WordPress hosting, and cloud deployments. The plugin communicates with our monitoring platform regardless of where your site is hosted.",
    },
  ];



  return (
    <div className="min-h-screen flex flex-col bg-background">
      {import.meta.env.DEV && (
        <div style={{position: 'fixed', bottom: '10px', right: '10px', padding: '5px', background: 'red', color: 'white', zIndex: 999}}>
          Variant: {variant}
        </div>
      )}
      <Helmet>
        <title>ShadowScan — Managed WordPress Security &amp; Response</title>
        <meta
          name="description"
          content="Managed WordPress security with continuous monitoring, application-level protections, and human oversight."
        />
        <link rel="canonical" href="https://shadowscan.com.au/" />
        <meta property="og:title" content="ShadowScan — Managed WordPress Security &amp; Response" />
        <meta property="og:description" content="Managed WordPress security with continuous monitoring, application-level protections, and human oversight." />
        <meta property="og:url" content="https://shadowscan.com.au/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "ShadowScan",
          url: "https://shadowscan.com.au/"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ShadowScan",
          url: "https://shadowscan.com.au/",
          logo: "https://shadowscan.com.au/favicon.png",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: "support@shadowscan.com.au",
            areaServed: "AU",
            availableLanguage: ["English"]
          }
        })}</script>
      </Helmet>
      <Navigation />
      <main className="flex-grow">
        <div className="relative z-0 overflow-hidden">
          {/* Blue background highlights */}
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl -z-10" />

          {/* Hero Section */}
        
<section className="pt-24 pb-16 lg:pt-32 lg:pb-24 animate-fade-in">
  <div className="container mx-auto px-4">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="text-center lg:text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-sm text-primary mb-6">
          <span className="flex relative h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span>{config.serviceStatus}</span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold leading-tight tracking-tight">
          {config.headline}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          {config.subheadline}
        </p>
        <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4">
          <Button
            size="lg"
            asChild
            className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg hover:shadow-accent transition-shadow"
            onClick={() =>
              trackCta("hero-primary", config.ctaText, config.ctaHref)
            }
          >
            <Link to={config.ctaHref}>{config.ctaText}</Link>
          </Button>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          {config.trustBadge}
        </p>
      </div>
      <div className="hidden lg:block">
        <HeroVisualization />
      </div>
    </div>
  </div>
</section>

        {/* Built For Section */}
        <section className="py-8 border-t border-border/40">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                {config.builtForHeading}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {config.builtForBody}
              </p>
            </div>
          </div>
        </section>

        {/* Problem Statement Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="max-w-xl">
                <h2 className="text-3xl font-heading font-bold">
                  {config.problemHeadline}
                </h2>
                <p className="mt-4 text-muted-foreground">
                  {config.problemBody}
                </p>
              </div>
              <div className="hidden md:block">
                <AbstractVisual />
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="hidden md:block md:order-last">
                <AbstractVisual />
              </div>
              <div className="max-w-xl">
                <h2 className="text-3xl font-heading font-bold">
                  {config.solutionHeadline}
                </h2>
                <p className="mt-4 text-muted-foreground">
                  {config.solutionBody}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-heading font-bold">How It Works</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((item) => (
                <div
                  key={item.step}
                  className="text-center p-6 rounded-xl cyber-card transition-all duration-300 animate-fade-in"
                >
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ShadowScan Service Levels */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-heading font-bold">ShadowScan Service Levels</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                ShadowScan spans free local hardening, hosted onboarding, monitored security, and analyst-led response support so you can choose the level that fits your site.
              </p>
              <p className="mt-2 text-sm text-muted-foreground italic">
                {config.serviceLevelsIntro}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* ShadowScan Guard */}
              <div className="cyber-card p-6 transition-all duration-300 animate-fade-in flex flex-col">
                <span className="inline-block self-start px-2 py-0.5 rounded-full bg-muted text-xs font-semibold uppercase tracking-wider mb-3">
                  Free Protection Layer
                </span>
                <h3 className="text-lg font-bold">ShadowScan Guard</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-grow">
                  Essential local hardening and basic visibility for WordPress sites.
                </p>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    className="w-full"
                    asChild
                    onClick={() =>
                      trackServiceCta("ShadowScan Guard", "Run Free Scan", "/scan", "service_levels")
                    }
                  >
                    <Link to="/scan">Run Free Scan</Link>
                  </Button>
                </div>
              </div>

              {/* Basic Hosting */}
              <div className="cyber-card p-6 transition-all duration-300 animate-fade-in flex flex-col">
                <span className="inline-block self-start px-2 py-0.5 rounded-full bg-muted text-xs font-semibold uppercase tracking-wider mb-3">
                  Hosted
                </span>
                <h3 className="text-lg font-bold">Basic Hosting</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-grow">
                  Managed WordPress hosting with ShadowScan Guard onboarding.
                </p>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    className="w-full"
                    asChild
                    onClick={() =>
                      trackServiceCta("Basic Hosting", "Explore Hosting", "/services", "service_levels")
                    }
                  >
                    <Link to="/services">Explore Hosting</Link>
                  </Button>
                </div>
              </div>

              {/* Essential */}
              <div className="cyber-card p-6 transition-all duration-300 animate-fade-in flex flex-col">
                <span className="inline-block self-start px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
                  Monitored
                </span>
                <h3 className="text-lg font-bold">Essential</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-grow">
                  Monitored WordPress security with validated guidance and managed oversight.
                </p>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    className="w-full"
                    asChild
                    onClick={() =>
                      trackServiceCta("Essential", "Explore Essential", "/services", "service_levels")
                    }
                  >
                    <Link to="/services">Explore Essential</Link>
                  </Button>
                </div>
              </div>

              {/* Premium */}
              <div className="cyber-card p-6 relative transition-all duration-300 border-primary/50 glow-border animate-fade-in flex flex-col">
                <span className="absolute -top-3 left-6 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  Recommended
                </span>
                <span className="inline-block self-start px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider mb-3">
                  Analyst-Led
                </span>
                <h3 className="text-lg font-bold">Premium</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-grow">
                  Higher-touch monitoring, response coordination, and analyst-led support.
                </p>
                <div className="mt-6">
                  <Button
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                    asChild
                    onClick={() =>
                      trackServiceCta("Premium", "Explore Premium", "/services", "service_levels")
                    }
                  >
                    <Link to="/services">Explore Premium</Link>
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="text-sm text-muted-foreground">
                Detailed pricing, hosting bundles, and service comparisons are available on the services page.{" "}
                <Link to="/services" className="text-primary hover:underline inline-flex items-center gap-1">
                  View Full Services &amp; Pricing
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Works With Your Stack Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-heading font-bold">
                Works Alongside Your Existing Stack
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                ShadowScan is designed to complement your existing tools, not
                replace them. It runs alongside your current hosting, firewalls,
                and performance plugins, adding a critical layer of
                application-specific security monitoring and response.
              </p>
            </div>
          </div>
        </section>

        {/* The Human Difference Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-heading font-bold">
                The Human Difference
              </h2>
              <p className="mt-4 max-w-2xl mx-auto text-muted-foreground leading-relaxed">
                Our Australian-based analysts validate high-risk alerts, separating critical threats from noise. You get actionable intelligence, not automated alert fatigue. This is how we reduce false-positive noise and turn data into decisions.
              </p>
            </div>
            <div className="mt-16">
              <AnalystWorkflowDiagram />
            </div>
          </div>
        </section>

        {/* Featured Managed Security Plans */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-2">
                Featured Managed Security Plans
              </h2>
              <p className="text-center text-muted-foreground mb-8">
                The two core monitored security tiers for WordPress environments.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div 
                  className="cyber-card p-8 transition-all duration-300 animate-fade-in cursor-pointer"
                  onClick={() => trackPricingCardClick("Essential")}
                >
                  <h3 className="text-2xl font-bold">Essential</h3>
                  <p className="mt-2 text-muted-foreground">
                    Monitored WordPress security with human-validated alerts.
                  </p>
                  <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Continuous log analysis and monitoring
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Vulnerability intelligence and prioritisation
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Human-validated alerts
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Guided remediation recommendations
                    </li>
                  </ul>
                  <div className="mt-8">
                    <Button
                      variant="outline"
                      className="w-full"
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                        trackCta("plan-section", "View Essential", "/services")
                      }}
                    >
                      <Link to="/services">View Details</Link>
                    </Button>
                  </div>
                </div>
                <div 
                  className="cyber-card p-8 relative transition-all duration-300 border-primary/50 glow-border animate-fade-in cursor-pointer"
                  onClick={() => trackPricingCardClick("Premium")}
                >
                  <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Recommended
                  </span>
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6 text-primary" />
                    <span>Premium</span>
                  </h3>
                  <p className="mt-2 text-muted-foreground">
                    Managed incident response with analyst-led investigation and remediation support.
                  </p>
                  <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Everything in Essential
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Included incident response hours
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Analyst-led monitoring and investigation
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Post-incident review and tuning
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      Priority support response
                    </li>
                  </ul>
                  <div className="mt-8">
                    <Button
                      className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                        trackCta("plan-section", "View Premium", "/services")
                      }}
                    >
                      <Link to="/services">View Details</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        

        {/* FAQ Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              <Accordion 
                type="single" 
                collapsible 
                className="w-full space-y-4"
                onValueChange={(value) => trackFaqOpen(value)}
              >
                {faqs.map((faq) => (
                  <AccordionItem value={faq.question} key={faq.question} className="border-border bg-card rounded-xl px-6">
                    <AccordionTrigger className="text-left font-semibold text-foreground">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 animate-fade-in">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center rounded-2xl border border-primary/30 bg-muted/20 p-12">
              <h2 className="text-3xl font-heading font-bold">
                Ready to Secure Your WordPress Site?
              </h2>
              <p className="mt-4 text-muted-foreground">
                Get managed WordPress security with human oversight from our
                Australian team.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg"
                  asChild
                  onClick={() =>
                    trackCta("final-cta", config.finalCtaText, config.finalCtaHref)
                  }
                >
                  <Link to={config.finalCtaHref}>
                    {config.finalCtaText}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
      <Footer />
    </div>
  );
};

export default Index;
